import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { PLAYER_GROUPS, PLAYER_ACCOUNT, groupForPath } from "@/lib/navigation";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, Menu, X, ChevronDown, UserRound } from "lucide-react";

const linkCls = ({ isActive }) =>
  `px-3 py-2 rounded-md text-[13px] whitespace-nowrap font-medium tracking-wide transition-colors ${
    isActive ? "text-[#00F076]" : "text-slate-300 hover:text-white"
  }`;

export default function Nav() {
  const { user, logout } = useAuth();
  const { lang, setLang, t } = useLang();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const L = (o) => o[lang] || o.en;
  const doLogout = () => { logout(); nav("/", { replace: true }); };

  const isClub = user?.role === "club";
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    if (!isClub) return;
    const tick = () => api.get("/club/notifications").then((r) => setUnread(r.data.unread)).catch(() => {});
    tick(); const id = setInterval(tick, 60000);
    window.addEventListener("pp:notifications", tick);
    return () => { clearInterval(id); window.removeEventListener("pp:notifications", tick); };
  }, [isClub]);

  const clubItems = [
    { to: "/club", label: t.nav.club, badge: unread },
    { to: "/club/compare", label: t.nav.compare },
    { to: "/billing", label: t.nav.billing },
  ];
  const activeGroup = !isClub && user ? groupForPath(pathname) : null;
  const groupActive = (g) => (g.to ? pathname === g.to : g.items.some((i) => pathname.startsWith(i.to)));

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[#090C10]/85 border-b border-[#1E2638]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to={user ? (isClub ? "/club" : "/dashboard") : "/"} data-testid="nav-brand" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#00F076] flex items-center justify-center text-black font-black">D</div>
          <span className="font-display font-black text-xl uppercase tracking-tight">dl<span className="text-[#00F076]">sport</span></span>
          {isClub && <span className="ml-1 px-2 py-0.5 rounded-full border border-[#00F076]/40 text-[#00F076] text-[10px] font-mono-tech uppercase" data-testid="nav-club-badge">{user.club_name}</span>}
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {!user && <NavLink to="/pricing" className={linkCls} data-testid="nav-pricing">{t.nav.pricing}</NavLink>}
          {isClub && clubItems.map((it) => (
            <NavLink key={it.to} to={it.to} className={linkCls} data-testid={`nav-${it.to.slice(1)}`}>
              {it.label}{it.badge > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#FF2E54] text-white text-[9px] font-bold align-middle" data-testid="nav-alert-badge">{it.badge}</span>}
            </NavLink>
          ))}
          {user && !isClub && PLAYER_GROUPS.map((g) => g.to ? (
            <NavLink key={g.id} to={g.to} className={linkCls} data-testid={`nav-group-${g.id}`}><span className="inline-flex items-center gap-1.5"><g.icon className="w-4 h-4" />{L(g)}</span></NavLink>
          ) : (
            <DropdownMenu key={g.id}>
              <DropdownMenuTrigger asChild>
                <button data-testid={`nav-group-${g.id}`} className={`px-3 py-2 rounded-md text-[13px] font-medium tracking-wide inline-flex items-center gap-1.5 transition-colors ${groupActive(g) ? "text-[#00F076]" : "text-slate-300 hover:text-white"}`}>
                  <g.icon className="w-4 h-4" />{L(g)}<ChevronDown className="w-3 h-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 bg-[#121721] border-[#1E2638] text-white p-2">
                <div className="px-2 pt-1 pb-2 text-[10px] font-mono-tech uppercase tracking-wider text-slate-500">{lang === "fr" ? g.frDesc : g.enDesc}</div>
                {g.items.map((it) => (
                  <DropdownMenuItem key={it.to} onClick={() => nav(it.to)} data-testid={`nav-item-${it.to.slice(1)}`} className="rounded-md px-2 py-2 cursor-pointer focus:bg-[#00F076]/10 focus:text-white">
                    <it.icon className="w-4 h-4 text-[#00F076] mr-2 shrink-0" />
                    <div><div className="text-sm font-semibold">{L(it)}</div><div className="text-[11px] text-slate-400">{lang === "fr" ? it.frDesc : it.enDesc}</div></div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="hidden sm:flex items-center rounded-full border border-[#1E2638] p-0.5" data-testid="language-switcher">
            <button onClick={() => setLang("fr")} data-testid="language-switcher-fr" className={`px-2.5 py-1 text-xs font-mono-tech rounded-full ${lang === "fr" ? "bg-[#00F076] text-black" : "text-slate-300"}`}>FR</button>
            <button onClick={() => setLang("en")} data-testid="language-switcher-en" className={`px-2.5 py-1 text-xs font-mono-tech rounded-full ${lang === "en" ? "bg-[#00F076] text-black" : "text-slate-300"}`}>EN</button>
          </div>

          {user && !isClub && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-account" className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#1E2638] pl-1 pr-3 py-1 text-xs text-slate-200 hover:border-[#00F076]/50">
                  <span className="w-7 h-7 rounded-full bg-[#00F076]/15 border border-[#00F076]/40 flex items-center justify-center"><UserRound className="w-4 h-4 text-[#00F076]" /></span>
                  <span className="max-w-[110px] truncate">{user.full_name?.split(" ")[0]}</span><ChevronDown className="w-3 h-3 opacity-60" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#121721] border-[#1E2638] text-white p-2">
                {PLAYER_ACCOUNT.map((it) => (
                  <DropdownMenuItem key={it.to} onClick={() => nav(it.to)} data-testid={`nav-account-${it.to.slice(1)}`} className="rounded-md px-2 py-2 cursor-pointer focus:bg-[#00F076]/10 focus:text-white"><it.icon className="w-4 h-4 text-[#00F076] mr-2" />{L(it)}</DropdownMenuItem>
                ))}
                <DropdownMenuSeparator className="bg-[#1E2638]" />
                <DropdownMenuItem onClick={doLogout} data-testid="nav-logout" className="rounded-md px-2 py-2 cursor-pointer text-slate-300 focus:bg-[#FF2E54]/10 focus:text-white"><LogOut className="w-4 h-4 mr-2" />{t.nav.logout}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {user && isClub && (
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="nav-logout" className="hidden md:inline-flex text-slate-300 hover:text-white"><LogOut className="w-4 h-4 mr-1" /> {t.nav.logout}</Button>
          )}
          {!user && (
            <div className="hidden sm:flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => nav("/auth?mode=login")} data-testid="nav-login" className="text-slate-300">{t.nav.login}</Button>
              <Button size="sm" onClick={() => nav("/auth?mode=register")} data-testid="nav-register" className="bg-[#00F076] hover:bg-[#00C963] text-black font-semibold">{t.nav.register}</Button>
            </div>
          )}
          {user && (
            <button className="md:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
          )}
        </div>
      </div>

      {activeGroup && activeGroup.items.length > 0 && (
        <div className="hidden md:block border-t border-[#1E2638]/60 bg-[#0D1118]" data-testid="nav-subbar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 h-10 overflow-x-auto">
            <span className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500 mr-2 inline-flex items-center gap-1"><activeGroup.icon className="w-3 h-3" />{L(activeGroup)}</span>
            {activeGroup.items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={`nav-sub-${it.to.slice(1)}`} className={({ isActive }) => `px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${isActive ? "bg-[#00F076] text-black" : "text-slate-300 hover:bg-white/5"}`}>
                <span className="inline-flex items-center gap-1.5"><it.icon className="w-3.5 h-3.5" />{L(it)}</span>
              </NavLink>
            ))}
          </div>
        </div>
      )}

      {open && user && (
        <div className="md:hidden border-t border-[#1E2638] bg-[#090C10] px-4 py-3 space-y-3 max-h-[80vh] overflow-auto" data-testid="nav-mobile-menu">
          {isClub ? clubItems.map((it) => (
            <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className={linkCls} data-testid={`nav-mobile-${it.to.slice(1)}`}><div className="py-2">{it.label}</div></NavLink>
          )) : (
            <>
              {PLAYER_GROUPS.map((g) => (
                <div key={g.id}>
                  {g.to ? (
                    <NavLink to={g.to} onClick={() => setOpen(false)} className={linkCls} data-testid={`nav-mobile-${g.to.slice(1)}`}><div className="py-2 inline-flex items-center gap-2 text-base"><g.icon className="w-4 h-4" />{L(g)}</div></NavLink>
                  ) : (
                    <>
                      <div className="px-3 pt-2 text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500 inline-flex items-center gap-1"><g.icon className="w-3 h-3" />{L(g)}</div>
                      {g.items.map((it) => (
                        <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className={linkCls} data-testid={`nav-mobile-${it.to.slice(1)}`}>
                          <div className="py-1.5 pl-3 flex items-center gap-2"><it.icon className="w-4 h-4 text-[#00F076]" /><span>{L(it)}</span><span className="text-[11px] text-slate-500">· {lang === "fr" ? it.frDesc : it.enDesc}</span></div>
                        </NavLink>
                      ))}
                    </>
                  )}
                </div>
              ))}
              <div className="border-t border-[#1E2638] pt-2">
                {PLAYER_ACCOUNT.map((it) => <NavLink key={it.to} to={it.to} onClick={() => setOpen(false)} className={linkCls} data-testid={`nav-mobile-${it.to.slice(1)}`}><div className="py-1.5 flex items-center gap-2"><it.icon className="w-4 h-4" />{L(it)}</div></NavLink>)}
              </div>
            </>
          )}
          <div className="flex items-center justify-between border-t border-[#1E2638] pt-3">
            <div className="flex items-center rounded-full border border-[#1E2638] p-0.5">
              <button onClick={() => setLang("fr")} className={`px-2.5 py-1 text-xs font-mono-tech rounded-full ${lang === "fr" ? "bg-[#00F076] text-black" : "text-slate-300"}`}>FR</button>
              <button onClick={() => setLang("en")} className={`px-2.5 py-1 text-xs font-mono-tech rounded-full ${lang === "en" ? "bg-[#00F076] text-black" : "text-slate-300"}`}>EN</button>
            </div>
            <Button variant="ghost" size="sm" onClick={doLogout} data-testid="nav-mobile-logout" className="text-slate-300"><LogOut className="w-4 h-4 mr-1" />{t.nav.logout}</Button>
          </div>
        </div>
      )}
    </header>
  );
}
